import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Eye, 
  Edit, 
  Search, 
  Filter, 
  Download, 
  Package, 
  Clock, 
  CheckCircle, 
  TrendingUp,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileImage,
  MoreVertical,
  Trash2,
  Send,
  Factory,
  Wallet,
  Truck
} from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';

const AdminOrders = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [orders, setOrders] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);
  const [includeSamples, setIncludeSamples] = useState(true);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    paymentMethod: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateForm, setUpdateForm] = useState({
    status: '',
    trackingNumber: '',
    carrier: '',
    adminNotes: '',
    project_name: ''
  });

  useEffect(() => {
    // Quando cambiano i filtri (ricerca/status/payment), torna sempre a pagina 1
    // così la ricerca server-side considera tutti i risultati
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      loadOrders();
    }
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line
  }, [pagination.page]);

  const loadOrders = async () => {
    try {
      setLoading(true);

      // Call real admin API for orders + sample orders in parallel
      const { adminAPI } = await import('../../services/api');
      const [result, sampleRes] = await Promise.all([
        adminAPI.getOrders(filters, pagination.page, pagination.limit),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/sample-orders`, {
          headers: { Authorization: `Bearer ${adminToken}` },
        }).then((r) => (r.ok ? r.json() : { data: [] })).catch(() => ({ data: [] })),
      ]);

      if (result.success) {
        setOrders(result.orders || []);
        setPagination(prev => ({ 
          ...prev, 
          total: result.totalCount || 0 
        }));
      } else {
        throw new Error(result.message || 'Errore nel caricamento ordini');
      }

      // Normalizza sample orders in shape "order-like" per l'unificazione
      const normalized = (sampleRes.data || []).map((s) => ({
        _isSample: true,
        id: s.id,
        orderNumber: `CAMP-${(s.id || '').slice(-6).toUpperCase()}`,
        status: s.status,
        items: (s.items || []).map((it) => ({
          name: `${it.liquor_name} · ${it.format}`,
          quantity: it.quantity,
        })),
        billingAddress: {
          firstName: s.recipient_name || '',
          lastName: '',
        },
        userEmail: s.recipient_email || s.agentEmail || '',
        agentName: s.agentName,
        agentEmail: s.agentEmail,
        agentCode: s.agentCode,
        recipient_city: s.shipping_city,
        paymentInfo: { method: 'campione', status: '—' },
        paymentStatus: '',
        totalAmount: 0,
        totalBottles: s.total_bottles || 0,
        createdAt: s.created_at,
      }));
      setSampleOrders(normalized);

    } catch (error) {
      console.error('Error loading orders:', error);
      toast({
        title: "Errore nel caricamento",
        description: error.message || "Impossibile caricare gli ordini",
        variant: "destructive"
      });
      
      // Fallback to empty state if API fails
      setOrders([]);
      setSampleOrders([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const downloadLabelFile = async (labelImagePath) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const fileUrl = `${backendUrl}/api/upload/serve/${labelImagePath}`;
      
      // Create download link
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = labelImagePath.split('/').pop(); // Get filename from path
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "File scaricato",
        description: "Il file dell'etichetta è stato scaricato",
        variant: "default",
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: "Errore",
        description: "Errore nel download del file",
        variant: "destructive",
      });
    }
  };

  const handleUpdateOrder = async (orderId, updateData) => {
    try {
      setUpdating(orderId);
      
      // Verify admin token exists
      if (!adminToken) {
        throw new Error('Token amministratore non valido. Effettua nuovamente il login.');
      }
      
      // Call update API with admin token
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore nell\'aggiornamento');
      }
      
      // Reload orders to get updated data
      await loadOrders();
      
      toast({
        title: "Ordine aggiornato",
        description: `Stato dell'ordine cambiato in: ${getStatusText(updateData.status)}`,
      });
      
      setShowUpdateModal(false);
      setSelectedOrder(null);
      
    } catch (error) {
      console.error('Error updating order:', error);
      toast({
        title: "Errore nell'aggiornamento",
        description: error.message || "Impossibile aggiornare l'ordine",
        variant: "destructive"
      });
    } finally {
      setUpdating(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'deposit_confirmed':
        return <Wallet className="w-4 h-4 text-emerald-600" />;
      case 'balance_confirmed':
        return <Wallet className="w-4 h-4 text-green-600" />;
      case 'waiting_label':
        return <FileImage className="w-4 h-4 text-orange-600" />;
      case 'payment_confirmed':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'procurement':
        return <Truck className="w-4 h-4 text-cyan-600" />;
      case 'in_production':
        return <Factory className="w-4 h-4 text-amber-600" />;
      case 'shipped':
        return <TrendingUp className="w-4 h-4 text-purple-600" />;
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'In attesa pagamento';
      case 'deposit_confirmed':
        return 'Acconto confermato (50%)';
      case 'balance_confirmed':
        return 'Saldo confermato';
      case 'waiting_label':
        return 'In attesa dei file';
      case 'payment_confirmed':
        return 'Pagamento confermato';
      case 'procurement':
        return 'Approvvigionamento';
      case 'in_production':
        return 'In Produzione';
      case 'production_completed':
        return 'Produzione completata';
      case 'to_ship':
        return 'Da spedire';
      case 'to_ship_gift':
        return 'Da spedire omaggio';
      case 'pickup_at_location':
        return 'Ritiro in sede';
      case 'shipped':
        return 'Spedito';
      case 'delivered':
        return 'Consegnato';
      case 'cancelled':
        return 'Annullato';
      // Sample-order statuses
      case 'pending':
        return 'Da approvare';
      case 'approved':
        return 'Approvato';
      case 'in_preparation':
        return 'In preparazione';
      case 'rejected':
        return 'Rifiutato';
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'deposit_confirmed':
        return 'bg-emerald-100 text-emerald-800';
      case 'balance_confirmed':
        return 'bg-green-100 text-green-800';
      case 'waiting_label':
        return 'bg-orange-100 text-orange-800';
      case 'payment_confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'procurement':
        return 'bg-cyan-100 text-cyan-800';
      case 'in_production':
        return 'bg-amber-100 text-amber-800';
      case 'production_completed':
        return 'bg-lime-100 text-lime-800';
      case 'to_ship':
        return 'bg-indigo-100 text-indigo-800';
      case 'to_ship_gift':
        return 'bg-pink-100 text-pink-800';
      case 'pickup_at_location':
        return 'bg-violet-100 text-violet-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-amber-100 text-amber-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'in_preparation':
        return 'bg-teal-100 text-teal-800';
      case 'rejected':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openUpdateModal = (order) => {
    setSelectedOrder(order);
    setUpdateForm({
      status: order.status,
      trackingNumber: order.shippingInfo?.trackingNumber || '',
      carrier: order.shippingInfo?.carrier || '',
      adminNotes: order.adminNotes || '',
      project_name: order.project_name || ''
    });
    setShowUpdateModal(true);
  };

  const filteredOrders = [
    ...orders,
    ...(includeSamples ? sampleOrders : []),
  ]
    .filter(order => {
      // NOTA: la ricerca è ora eseguita SERVER-SIDE per orders paginati (vede tutti i clienti).
      // Per i sample orders (caricati in blocco separato senza paginazione lato server),
      // applichiamo la stessa ricerca client-side.
      const customerName = `${order.billingAddress?.firstName || ''} ${order.billingAddress?.lastName || ''}`.trim();
      const agentName = order._isSample ? (order.agentName || '') : '';
      const searchLower = (filters.search || '').toLowerCase();

      // Server-side matched orders passano sempre; per i sample e come safety net applichiamo il filtro se search attivo
      const matchesSearch = !searchLower || order._isSample === false || !order._isSample
        ? (!searchLower ||
            (order.orderNumber || '').toLowerCase().includes(searchLower) ||
            customerName.toLowerCase().includes(searchLower) ||
            (order.userEmail || '').toLowerCase().includes(searchLower) ||
            (order.project_name && order.project_name.toLowerCase().includes(searchLower)) ||
            agentName.toLowerCase().includes(searchLower) ||
            // Fallback su campi customer_data se popolati (compat backend server-side)
            ((order.customer_data?.name || '').toLowerCase().includes(searchLower)) ||
            ((order.customer_data?.company_name || '').toLowerCase().includes(searchLower)) ||
            ((order.customer_data?.business_name || '').toLowerCase().includes(searchLower)))
        : true;

      const matchesStatus = !filters.status || order.status === filters.status;
      const matchesPayment = !filters.paymentMethod || order.paymentInfo?.method === filters.paymentMethod;

      return matchesSearch && matchesStatus && matchesPayment;
    })
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gestione Ordini</h1>
            <p className="mt-2 text-gray-600">Visualizza e gestisci tutti gli ordini del sistema</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={loadOrders}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cerca</label>
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Numero ordine, cliente..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Stato</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti gli stati</option>
                <option value="pending_payment">In attesa pagamento</option>
                <option value="deposit_confirmed">💰 Acconto confermato (50%)</option>
                <option value="balance_confirmed">💰 Saldo confermato</option>
                <option value="waiting_label">📁 In attesa dei file</option>
                <option value="payment_confirmed">Pagamento confermato</option>
                <option value="procurement">📦 Approvvigionamento</option>
                <option value="in_production">🏭 In Produzione</option>
                <option value="production_completed">✅ Produzione completata</option>
                <option value="to_ship">📦 Da spedire</option>
                <option value="to_ship_gift">🎁 Da spedire omaggio</option>
                <option value="pickup_at_location">🏢 Ritiro in sede</option>
                <option value="shipped">Spedito</option>
                <option value="delivered">Consegnato</option>
                <option value="cancelled">Annullato</option>
                <option value="pending">🧪 Camp: Da approvare</option>
                <option value="approved">🧪 Camp: Approvato</option>
                <option value="in_preparation">🧪 Camp: In preparazione</option>
                <option value="rejected">🧪 Camp: Rifiutato</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pagamento</label>
              <select
                value={filters.paymentMethod}
                onChange={(e) => setFilters(prev => ({ ...prev, paymentMethod: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Tutti i metodi</option>
                <option value="bank_transfer">Bonifico</option>
                <option value="credit_card">Carta di credito</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ status: '', paymentMethod: '', search: '' })}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Pulisci Filtri
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSamples}
                onChange={(e) => setIncludeSamples(e.target.checked)}
                data-testid="toggle-include-samples"
                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Includi ordini campioni
            </label>
            <span className="text-xs text-gray-500">({sampleOrders.length} campioni)</span>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <LoadingSpinner message="Caricamento ordini..." />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ordine
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cliente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Pagamento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Totale
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.map((order) => (
                      <tr key={order.id} className={`hover:bg-gray-50 ${order._isSample ? 'bg-purple-50/30' : ''}`} data-testid={order._isSample ? `sample-row-${order.id}` : `order-row-${order.id}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 mb-1">
                            {order._isSample ? (
                              <>
                                <Link
                                  to="/admin/sample-orders"
                                  className="text-sm font-medium text-purple-700 hover:text-purple-900 hover:underline cursor-pointer"
                                >
                                  #{order.orderNumber}
                                </Link>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-fuchsia-100 text-fuchsia-800">
                                  🧪 CAMPIONE
                                </span>
                              </>
                            ) : (
                              <>
                                <Link
                                  to={`/admin/orders/${order.id}`}
                                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                >
                                  #{order.orderNumber}
                                </Link>
                                {order.project_name && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                    📋 {order.project_name}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 space-y-1">
                            {order._isSample ? (
                              <>
                                <div className="text-xs text-gray-600">
                                  <span className="font-medium">{order.totalBottles}</span> bottiglie campione
                                </div>
                                {order.items && order.items.slice(0, 2).map((it, i) => (
                                  <div key={i} className="text-xs text-gray-400">• {it.name} × {it.quantity}</div>
                                ))}
                                {order.items && order.items.length > 2 && (
                                  <div className="text-xs text-gray-400">+{order.items.length - 2} altri</div>
                                )}
                              </>
                            ) : (
                              <>
                                {order.items && order.items.slice(0, 1).map((item, index) => {
                                  const liquorType = item.liquorType;
                                  const selectedIngredients = item.selectedIngredients || [];
                                  const labelText = item.labelText;
                                  const productName = item.name || (liquorType ? `${liquorType} Personalizzato` : 'Prodotto Personalizzato');
                                  return (
                                    <div key={index}>
                                      <div className="font-medium text-xs">{productName}</div>
                                      {liquorType && (
                                        <div className="text-xs text-gray-400">Base: {liquorType}</div>
                                      )}
                                      {selectedIngredients.length > 0 && (
                                        <div className="text-xs text-gray-400">
                                          {selectedIngredients.slice(0, 2).map(ing => typeof ing === 'string' ? ing : ing.name || ing).join(', ')}
                                          {selectedIngredients.length > 2 && '...'}
                                        </div>
                                      )}
                                      {labelText && (
                                        <div className="text-xs text-gray-400">&quot;{labelText.substring(0, 20)}{labelText.length > 20 ? '...' : ''}&quot;</div>
                                      )}
                                    </div>
                                  );
                                })}
                                {order.items && order.items.length > 1 && (
                                  <div className="text-xs text-gray-400">+{order.items.length - 1} altri</div>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {order._isSample ? (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {order.billingAddress?.firstName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {order.recipient_city ? `${order.recipient_city} · ` : ''}Destinatario
                              </div>
                              <div className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-[10px] font-medium text-purple-800">
                                👤 Agente: {order.agentName || '—'}
                                {order.agentCode && <span className="font-mono text-purple-500">· {order.agentCode}</span>}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-sm font-medium text-gray-900">
                                {order.billingAddress?.firstName} {order.billingAddress?.lastName}
                              </div>
                              <div className="text-sm text-gray-500">{order.userEmail}</div>
                            </>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(order.status)}
                            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="capitalize">{order.paymentInfo?.method?.replace('_', ' ') || 'N/A'}</div>
                          <div className={`text-xs ${
                            order.paymentStatus === 'completed' || order.status === 'payment_confirmed' || order.paymentInfo?.status === 'completed'
                              ? 'text-green-600 font-medium' 
                              : 'text-gray-500'
                          }`}>
                            {order.paymentStatus === 'completed' || order.paymentInfo?.status === 'completed' || order.status === 'payment_confirmed'
                              ? 'Completato' 
                              : (order.paymentInfo?.status || 'Pending')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {order._isSample ? <span className="text-gray-400 italic">Gratuito</span> : `€${(order.totalAmount || 0).toFixed(2)}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(order.createdAt).toLocaleDateString('it-IT')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {order._isSample ? (
                              <Link
                                to="/admin/sample-orders"
                                className="text-purple-600 hover:text-purple-900 p-1 rounded inline-flex items-center gap-1"
                                title="Gestisci in Richieste campioni"
                              >
                                <Eye className="w-4 h-4" />
                                <span className="text-xs">Gestisci</span>
                              </Link>
                            ) : (
                              <>
                                {/* Azioni primarie sempre visibili */}
                                <Link
                                  to={`/admin/orders/${order.id}`}
                                  className="text-purple-600 hover:text-purple-900 p-1 rounded"
                                  title="Vedi dettaglio ordine"
                                >
                                  <Eye className="w-4 h-4" />
                                </Link>
                                <button
                                  onClick={() => openUpdateModal(order)}
                                  disabled={updating === order.id}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded"
                                  title="Modifica ordine"
                            >
                              {updating === order.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Edit className="w-4 h-4" />
                              )}
                            </button>
                            
                            {/* Dropdown per azioni secondarie */}
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdown(openDropdown === order.id ? null : order.id);
                                }}
                                className="text-gray-600 hover:text-gray-900 p-1"
                                title="Altre azioni"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              
                              {openDropdown === order.id && (
                                <>
                                  {/* Overlay per chiudere */}
                                  <div 
                                    className="fixed inset-0 z-10" 
                                    onClick={() => setOpenDropdown(null)}
                                  />
                                  
                                  {/* Menu dropdown */}
                                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Inviare email di promemoria pagamento a ${order.userEmail}?`)) {
                                          try {
                                            const response = await fetch(
                                              `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${order.id}/send-payment-reminder`,
                                              {
                                                method: 'POST',
                                                headers: {
                                                  'Authorization': `Bearer ${adminToken}`,
                                                  'Content-Type': 'application/json'
                                                }
                                              }
                                            );
                                            
                                            if (response.ok) {
                                              toast({
                                                title: "Email inviata",
                                                description: `Email di promemoria inviata a ${order.userEmail}`
                                              });
                                            } else {
                                              const errorData = await response.json();
                                              throw new Error(errorData.detail || 'Errore invio email');
                                            }
                                          } catch (error) {
                                            toast({
                                              title: "Errore",
                                              description: error.message || "Impossibile inviare l'email",
                                              variant: "destructive"
                                            });
                                          }
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <Send className="w-4 h-4 text-green-600" />
                                      <span>Promemoria Pagamento</span>
                                    </button>
                                    
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`Sei sicuro di voler scaricare il PDF dell'ordine #${order.orderNumber}?`)) {
                                          try {
                                            const response = await fetch(
                                              `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${order.id}/download-pdf`,
                                              {
                                                headers: {
                                                  'Authorization': `Bearer ${adminToken}`
                                                }
                                              }
                                            );
                                            
                                            if (response.ok) {
                                              const blob = await response.blob();
                                              const url = window.URL.createObjectURL(blob);
                                              const a = document.createElement('a');
                                              a.href = url;
                                              a.download = `ordine_${order.orderNumber}.pdf`;
                                              document.body.appendChild(a);
                                              a.click();
                                              window.URL.revokeObjectURL(url);
                                              document.body.removeChild(a);
                                              
                                              toast({
                                                title: "PDF scaricato",
                                                description: `PDF ordine #${order.orderNumber} scaricato con successo`
                                              });
                                            } else {
                                              throw new Error('Errore download PDF');
                                            }
                                          } catch (error) {
                                            toast({
                                              title: "Errore",
                                              description: "Impossibile scaricare il PDF",
                                              variant: "destructive"
                                            });
                                          }
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2"
                                    >
                                      <Download className="w-4 h-4 text-purple-600" />
                                      <span>Scarica PDF</span>
                                    </button>
                                    
                                    <div className="border-t border-gray-100 my-1"></div>
                                    
                                    <button
                                      onClick={async () => {
                                        if (window.confirm(`⚠️ ATTENZIONE: Sei sicuro di voler eliminare definitivamente l'ordine #${order.orderNumber}?\n\nQuesta azione non può essere annullata!`)) {
                                          try {
                                            setUpdating(order.id);
                                            
                                            const response = await fetch(
                                              `${process.env.REACT_APP_BACKEND_URL}/api/orders/admin/${order.id}`,
                                              {
                                                method: 'DELETE',
                                                headers: {
                                                  'Authorization': `Bearer ${adminToken}`
                                                }
                                              }
                                            );
                                            
                                            if (response.ok) {
                                              toast({
                                                title: "Ordine eliminato",
                                                description: `Ordine #${order.orderNumber} eliminato con successo`
                                              });
                                              
                                              // Reload orders
                                              loadOrders();
                                            } else {
                                              const error = await response.json();
                                              throw new Error(error.detail || 'Errore eliminazione');
                                            }
                                          } catch (error) {
                                            toast({
                                              title: "Errore",
                                              description: error.message || "Impossibile eliminare l'ordine",
                                              variant: "destructive"
                                            });
                                          } finally {
                                            setUpdating(null);
                                          }
                                        }
                                        setOpenDropdown(null);
                                      }}
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 flex items-center space-x-2 text-red-600"
                                      disabled={updating === order.id}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      <span>{updating === order.id ? 'Eliminando...' : 'Elimina'}</span>
                                    </button>
                                  </div>
                                </>
                              )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredOrders.length === 0 && (
                <div className="text-center py-12">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun ordine trovato</h3>
                  <p className="mt-1 text-sm text-gray-500">Prova a modificare i filtri di ricerca</p>
                </div>
              )}

              {/* Paginazione */}
              {filteredOrders.length > 0 && pagination.total > pagination.limit && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    {/* Mobile pagination */}
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Precedente
                    </button>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(Math.ceil(pagination.total / pagination.limit), prev.page + 1) }))}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Successivo
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Mostrando <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> a{' '}
                        <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> di{' '}
                        <span className="font-medium">{pagination.total}</span> ordini
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                          disabled={pagination.page === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Precedente</span>
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        
                        {/* Numeri pagina */}
                        {Array.from({ length: Math.ceil(pagination.total / pagination.limit) }, (_, i) => i + 1)
                          .filter(page => {
                            // Mostra sempre prima pagina, ultima, corrente e 1 adiacente
                            const totalPages = Math.ceil(pagination.total / pagination.limit);
                            return page === 1 || 
                                   page === totalPages || 
                                   Math.abs(page - pagination.page) <= 1;
                          })
                          .map((page, index, array) => {
                            // Aggiungi ellipsis se c'è un gap
                            const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                            
                            return (
                              <React.Fragment key={page}>
                                {showEllipsisBefore && (
                                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setPagination(prev => ({ ...prev, page }))}
                                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                    pagination.page === page
                                      ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                        
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, page: Math.min(Math.ceil(pagination.total / pagination.limit), prev.page + 1) }))}
                          disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Successivo</span>
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Update Modal */}
        {showUpdateModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[600px] max-w-[90vw] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Gestione Ordine #{selectedOrder.orderNumber}
                </h3>
                
                {/* Order Details Section */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-3">Dettagli Prodotti</h4>
                  <div className="space-y-3 max-h-40 overflow-y-auto">
                    {selectedOrder.items?.map((item, index) => {
                      // Use CORRECT ORDER structure
                      const liquorType = item.liquorType;
                      const selectedIngredients = item.selectedIngredients || [];
                      const bottleName = item.bottleType?.name || item.bottleType;
                      const labelText = item.labelText;
                      
                      const productName = item.name || (liquorType ? `${liquorType} Personalizzato` : 'Prodotto');

                      return (
                        <div key={index} className="text-sm border-b border-gray-200 pb-2">
                          <div className="font-medium text-gray-900">{productName}</div>
                          {liquorType && (
                            <div className="text-gray-600">Base: {liquorType}</div>
                          )}
                          {selectedIngredients.length > 0 && (
                            <div className="text-gray-600">
                              Botaniche: {selectedIngredients.map(ing => typeof ing === 'string' ? ing : ing.name || ing).join(', ')}
                            </div>
                          )}
                          {bottleName && (
                            <div className="text-gray-600">Bottiglia: {bottleName}</div>
                          )}
                          {labelText && (
                            <div className="text-gray-600">Etichetta: "{labelText}"</div>
                          )}
                          {(item.labelImage || item.bottlePreview) && (
                            <div className="text-gray-600">
                              <div className="text-sm font-medium mb-1">File Etichetta:</div>
                              <div className="flex flex-wrap gap-2">
                                {item.labelImage && (
                                  <button
                                    onClick={() => {
                                      const url = item.labelImage.startsWith('http') 
                                        ? item.labelImage 
                                        : `${process.env.REACT_APP_BACKEND_URL}/api/upload/serve/${item.labelImage}`;
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = item.labelImage.split('/').pop();
                                      link.target = '_blank';
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200 transition-colors"
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Etichetta Originale
                                  </button>
                                )}
                                {item.bottlePreview && (
                                  <a
                                    href={item.bottlePreview.startsWith('http') 
                                      ? item.bottlePreview 
                                      : `${process.env.REACT_APP_BACKEND_URL}/api/upload/serve/${item.bottlePreview}`}
                                    download
                                    className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded hover:bg-purple-200 transition-colors"
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Anteprima 3D
                                  </a>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="text-gray-500">
                            Quantità: {item.quantity || 1} • €{((item.totalPrice || 0) * (item.quantity || 1)).toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cliente: {selectedOrder.customerName || 'N/A'}</span>
                      <span className="font-medium text-gray-900">Totale: €{selectedOrder.totalAmount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Indirizzi di spedizione e fatturazione */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Indirizzo di spedizione */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">📦 Indirizzo di Spedizione</h4>
                      {selectedOrder.shippingAddress ? (
                        <div className="text-sm text-gray-600 space-y-1">
                          <p className="font-medium">{selectedOrder.shippingAddress.firstName} {selectedOrder.shippingAddress.lastName}</p>
                          {selectedOrder.shippingAddress.company && <p>{selectedOrder.shippingAddress.company}</p>}
                          <p>{selectedOrder.shippingAddress.street} {selectedOrder.shippingAddress.streetNumber}</p>
                          <p>{selectedOrder.shippingAddress.zipCode} {selectedOrder.shippingAddress.city} ({selectedOrder.shippingAddress.province})</p>
                          <p>{selectedOrder.shippingAddress.phone}</p>
                          {selectedOrder.shippingAddress.notes && <p className="italic">Note: {selectedOrder.shippingAddress.notes}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Indirizzo di spedizione non disponibile</p>
                      )}
                    </div>
                    
                    {/* Indirizzo di fatturazione */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">🧾 Indirizzo di Fatturazione</h4>
                      {selectedOrder.billingAddress ? (
                        <div className="text-sm text-gray-600 space-y-1">
                          <p className="font-medium">{selectedOrder.billingAddress.firstName} {selectedOrder.billingAddress.lastName}</p>
                          {selectedOrder.billingAddress.company && <p>{selectedOrder.billingAddress.company}</p>}
                          <p>{selectedOrder.billingAddress.street} {selectedOrder.billingAddress.streetNumber}</p>
                          <p>{selectedOrder.billingAddress.zipCode} {selectedOrder.billingAddress.city} ({selectedOrder.billingAddress.province})</p>
                          <p>{selectedOrder.billingAddress.phone}</p>
                          {selectedOrder.billingAddress.notes && <p className="italic">Note: {selectedOrder.billingAddress.notes}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">Indirizzo di fatturazione non disponibile</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Email del cliente */}
                  <div className="border-t pt-2">
                    <h4 className="font-semibold text-gray-900 mb-1">📧 Contatto</h4>
                    <p className="text-sm text-gray-600">{selectedOrder.userEmail}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stato</label>
                    <select
                      value={updateForm.status}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="pending_payment">In attesa pagamento</option>
                      <option value="deposit_confirmed">💰 Acconto confermato (50%)</option>
                      <option value="balance_confirmed">💰 Saldo confermato</option>
                      <option value="waiting_label">📁 In attesa dei file</option>
                      <option value="payment_confirmed">Pagamento confermato</option>
                      <option value="procurement">📦 Approvvigionamento</option>
                      <option value="in_production">🏭 In Produzione</option>
                      <option value="production_completed">✅ Produzione completata</option>
                      <option value="to_ship">📦 Da spedire</option>
                      <option value="to_ship_gift">🎁 Da spedire omaggio</option>
                      <option value="pickup_at_location">🏢 Ritiro in sede</option>
                      <option value="shipped">Spedito</option>
                      <option value="delivered">Consegnato</option>
                      <option value="cancelled">Annullato</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      📋 Nome Progetto <span className="text-gray-400 font-normal">(facoltativo)</span>
                    </label>
                    <input
                      type="text"
                      value={updateForm.project_name}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, project_name: e.target.value }))}
                      placeholder="Es: Amaro dei Lupi, Gin Botanico..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Identifica facilmente questo ordine personalizzato
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Numero Tracking</label>
                    <input
                      type="text"
                      value={updateForm.trackingNumber}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, trackingNumber: e.target.value }))}
                      placeholder="Es: SDA123456789"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Corriere</label>
                    <select
                      value={updateForm.carrier}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, carrier: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Seleziona corriere</option>
                      <option value="SDA">SDA</option>
                      <option value="Bartolini">Bartolini</option>
                      <option value="GLS">GLS</option>
                      <option value="DHL">DHL</option>
                      <option value="Poste Italiane">Poste Italiane</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note Admin</label>
                    <textarea
                      value={updateForm.adminNotes}
                      onChange={(e) => setUpdateForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                      rows={3}
                      placeholder="Note interne per questo ordine..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowUpdateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => handleUpdateOrder(selectedOrder.id, updateForm)}
                    disabled={updating}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {updating ? 'Aggiornamento...' : 'Aggiorna Ordine'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;